import React from 'react';
import { Box, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { formatCurrencyOrDash } from './leadsMeta';
import { LeadSummaryResponse } from '../../../services/marketingLeads';

const GOOD = '#22c55e';
const BAD = '#ef4444';

interface Tile {
  key: string;
  label: string;
  value: string;
  changePct: number | null;
  // Whether an increase in this metric is a good thing (leads) or a bad
  // thing (cost) — controls the delta color, matching the source design.
  higherIsBetter: boolean;
  icon: React.ReactNode;
  iconColor: string;
}

const DeltaBadge: React.FC<{ changePct: number | null; higherIsBetter: boolean; periodLabel: string }> = ({
  changePct,
  higherIsBetter,
  periodLabel,
}) => {
  // null = nothing in the previous period to compare against. The old
  // "+100%" here was the server's stand-in for that and made a brand-new
  // dashboard look like everything doubled.
  if (changePct === null) {
    return (
      <Box sx={{ color: dashboardTokens.textMuted, fontSize: 12, fontWeight: 500 }}>
        no data for {periodLabel}
      </Box>
    );
  }

  const isIncrease = changePct >= 0;
  const isGood = isIncrease === higherIsBetter;
  const color = changePct === 0 ? dashboardTokens.textMuted : isGood ? GOOD : BAD;
  const Arrow = isIncrease ? ArrowDropUpIcon : ArrowDropDownIcon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', color, fontSize: 12, fontWeight: 600 }}>
      {changePct !== 0 && <Arrow sx={{ fontSize: 16 }} />}
      {Math.abs(changePct)}% vs {periodLabel}
    </Box>
  );
};

const StatTile: React.FC<{ tile: Tile; periodLabel: string }> = ({ tile, periodLabel }) => (
  <Box
    sx={{
      flex: '1 1 200px',
      minWidth: 190,
      bgcolor: '#1c1c1c',
      border: `1px solid ${dashboardTokens.border}`,
      borderRadius: 3,
      p: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: dashboardTokens.textSecondary, fontWeight: 500 }}>
        {tile.label}
      </Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${tile.iconColor}22`,
          color: tile.iconColor,
        }}
      >
        {tile.icon}
      </Box>
    </Box>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#fff', lineHeight: 1.1, mb: 0.75 }}>
      {tile.value}
    </Typography>
    <DeltaBadge changePct={tile.changePct} higherIsBetter={tile.higherIsBetter} periodLabel={periodLabel} />
  </Box>
);

interface SummaryCardsProps {
  overall: LeadSummaryResponse['overall'] | null;
  // The range the summary was computed for — drives the heading and the
  // "vs previous N days" wording. Without it the heading said "This Week"
  // over a 30-day default range.
  range?: LeadSummaryResponse['range'] | null;
  /** ISO 4217 of the connected ad account — the money tiles are in it. */
  currency?: string;
}

const rangeHeading = (range?: LeadSummaryResponse['range'] | null): string => {
  if (!range?.from || !range?.to) return '';
  const from = new Date(range.from);
  const to = new Date(range.to);
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(from)} – ${fmt(to)}`;
};

export const SummaryCards: React.FC<SummaryCardsProps> = ({ overall, range, currency = 'GBP' }) => {
  const o = overall || {
    leads: 0,
    qualifiedLeads: 0,
    callsReceived: 0,
    costPerLead: 0,
    amountSpent: 0,
    spendDays: 0,
    changePct: { leads: null, qualifiedLeads: null, callsReceived: null, costPerLead: null, amountSpent: null },
  };
  // No campaign-days of spend stored for this range means the money tiles
  // have nothing measured behind them — "—", not a confident zero.
  const hasSpend = (o.spendDays ?? 0) > 0;
  const days = range?.days;
  const periodLabel = days ? `previous ${days} day${days === 1 ? '' : 's'}` : 'previous period';
  const heading = rangeHeading(range);

  const tiles: Tile[] = [
    {
      key: 'leads',
      label: 'Total Leads',
      value: String(o.leads),
      changePct: o.changePct.leads,
      higherIsBetter: true,
      icon: <PeopleAltOutlinedIcon fontSize="small" />,
      iconColor: '#60a5fa',
    },
    {
      key: 'qualifiedLeads',
      label: 'Qualified Leads',
      value: String(o.qualifiedLeads),
      changePct: o.changePct.qualifiedLeads,
      higherIsBetter: true,
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      iconColor: '#22c55e',
    },
    {
      key: 'callsReceived',
      label: 'Calls Received',
      value: String(o.callsReceived),
      changePct: o.changePct.callsReceived,
      higherIsBetter: true,
      icon: <PhoneOutlinedIcon fontSize="small" />,
      iconColor: '#a78bfa',
    },
    {
      key: 'costPerLead',
      label: 'Cost per Lead',
      value: formatCurrencyOrDash(o.costPerLead, currency, hasSpend),
      changePct: hasSpend ? o.changePct.costPerLead : null,
      higherIsBetter: false,
      icon: <PaidOutlinedIcon fontSize="small" />,
      iconColor: '#f59e0b',
    },
    {
      key: 'amountSpent',
      label: 'Total Spent',
      value: formatCurrencyOrDash(o.amountSpent, currency, hasSpend),
      changePct: hasSpend ? o.changePct.amountSpent : null,
      higherIsBetter: false,
      icon: <TrendingUpIcon fontSize="small" />,
      iconColor: '#ef4444',
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
          All Ads Campaigns <span style={{ color: dashboardTokens.textMuted, fontWeight: 500 }}>{heading ? `— ${heading}` : '— Summary'}</span>
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#60a5fa' }}>All Platform</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {tiles.map((tile) => (
          <StatTile key={tile.key} tile={tile} periodLabel={periodLabel} />
        ))}
      </Box>
    </Box>
  );
};
